import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Trash2, Search, RefreshCw, Mail, User, Building, Shield, Clock } from 'lucide-react';

export default function DeletedAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  const fetchDeletedAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/admin/deleted-accounts');
      setAccounts(res.data.deleted_accounts || []);
    } catch (err) {
      setError('Failed to load deleted accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedAccounts();
  }, []);

  const filteredAccounts = accounts.filter(acc => {
    const term = searchTerm.toLowerCase();
    return (
      (acc.email || '').toLowerCase().includes(term) ||
      (acc.full_name || '').toLowerCase().includes(term) ||
      (acc.company_name || '').toLowerCase().includes(term) ||
      (acc.plan || '').toLowerCase().includes(term) ||
      (acc.role || '').toLowerCase().includes(term)
    );
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Trash2 size={24} color="var(--error)" /> Deleted Accounts
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
            Accounts deleted in the last 30 days. Data retained for potential restoration.
          </p>
        </div>
        <button
          onClick={fetchDeletedAccounts}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 18px', borderRadius: '8px',
            border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
            color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '20px', maxWidth: '400px' }}>
        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by email, name, company, plan..."
          style={{
            width: '100%', padding: '10px 12px 10px 36px',
            background: 'var(--input-bg)', border: '1px solid var(--input-border)',
            borderRadius: '8px', color: 'var(--text-primary)', fontSize: '14px',
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', background: 'var(--error-bg)', border: '1px solid var(--error)', color: 'var(--error)', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div className="loader" style={{ margin: '0 auto' }} />
          <p style={{ color: 'var(--text-tertiary)', fontSize: '14px', marginTop: '16px' }}>Loading deleted accounts...</p>
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: '12px' }}>
          <Trash2 size={32} color="var(--text-tertiary)" style={{ marginBottom: '12px' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>No deleted accounts found.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)' }}>
                <th style={thStyle}>User ID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Full Name</th>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Plan</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Deleted At</th>
                <th style={thStyle}>Deleted By</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((acc) => (
                <tr key={acc.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  <td style={tdStyle}>{acc.original_user_id}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Mail size={12} color="var(--text-tertiary)" />
                      {acc.email}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <User size={12} color="var(--text-tertiary)" />
                      {acc.full_name || '—'}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Building size={12} color="var(--text-tertiary)" />
                      {acc.company_name || '—'}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px',
                      background: acc.plan === 'enterprise' ? 'var(--accent-amber-glow)' : 'var(--accent-glow)',
                      color: acc.plan === 'enterprise' ? 'var(--accent-amber)' : 'var(--accent-primary)',
                      fontSize: '11px', fontWeight: 600,
                    }}>
                      {acc.plan}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Shield size={12} color="var(--text-tertiary)" />
                      {acc.role}
                    </div>
                  </td>
                  <td style={tdStyle}>{acc.service_name || '—'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={12} color="var(--text-tertiary)" />
                      {acc.deleted_at ? new Date(acc.deleted_at).toLocaleString() : '—'}
                    </div>
                  </td>
                  <td style={tdStyle}>{acc.deleted_by || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '12px 14px',
  textAlign: 'left',
  color: 'var(--text-secondary)',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '12px 14px',
  color: 'var(--text-primary)',
  fontSize: '13px',
};
